import { X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '../../Shared/Input/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../Shared/Sheet/sheet';
import { SheetConfirmationCloseDialog } from '../SheetConfirmationCloseDialog';
import { ConnectRemoteMcpForm } from './ConnectRemoteMcpAction.form';
import { RemoteMcpActionTools } from './RemoteMcpActionTools';
import { recommendedSetupOf, RemoteMcpDto } from '@/Domain/api/Action/dto/RemoteMCP.dto';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { useConnectRemoteMcpActionForm } from '@/Domain/hooks/Action/RemoteMcp/useConnectRemoteMcpActionForm.hook';
import { useRemoteMcpActionForm } from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpActionForm.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type RemoteMcpActionSheetContentProps = {
  remoteMcpAction?: RemoteMcpDto;
  catalogEntryName?: string;
  closeModal: () => void;
  closeRequestRef: { current: () => void };
};

const RemoteMcpActionSheetContent = ({
  remoteMcpAction,
  catalogEntryName,
  closeModal,
  closeRequestRef,
}: RemoteMcpActionSheetContentProps) => {
  const { t } = useTranslation('remote_mcp');
  const { setParams } = useModalStore();

  const connect = useConnectRemoteMcpActionForm(remoteMcpAction);
  const { isDirty: connectFormIsDirty } = useFormState({ control: connect.form.control });
  const hasUnsavedConnectionChanges = () => {
    if (!connectFormIsDirty) return false;
    const values = connect.form.getValues();
    const comparableHeaders = (headers: RemoteMcpDto['headers']) =>
      headers.map(({ name, value, content }) => ({ name, value: value ?? content ?? '' }));
    if (!remoteMcpAction) {
      return (
        values.remote_mcp_url.length > 0 ||
        comparableHeaders(values.headers).some(({ name, value }) => name.length || value.length)
      );
    }
    return (
      values.remote_mcp_url !== remoteMcpAction.remote_mcp_url ||
      JSON.stringify(comparableHeaders(values.headers)) !==
        JSON.stringify(comparableHeaders(remoteMcpAction.headers))
    );
  };
  const {
    form,
    handleSubmit,
    onInvalid,
    isPending: isActionPending,
    isSubmissionPending: isActionSubmissionPending,
  } = useRemoteMcpActionForm(remoteMcpAction, {
    onSuccess: (savedAction) => {
      setParams({ remoteMcpAction: savedAction });
      if (!hasUnsavedConnectionChanges()) {
        connect.form.reset();
        closeModal();
      }
    },
  });
  const isPending = isActionPending || connect.isPending;

  const scrollContainerRef = useRef<HTMLElement>(null);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const handleConfirmClose = () => {
    setConfirmationModalOpen(false);
    form.reset();
    connect.form.reset();
    closeModal();
  };

  const { isDirty: actionFormIsDirty } = useFormState({ control: form.control });
  const handleClose = () => {
    if (isPending || isActionSubmissionPending() || connect.isSubmissionPending()) return;
    if (actionFormIsDirty || hasUnsavedConnectionChanges()) {
      setConfirmationModalOpen(true);
    } else {
      form.reset();
      connect.form.reset();
      closeModal();
    }
  };
  closeRequestRef.current = handleClose;

  return (
    <Form {...form}>
      <fieldset disabled={isPending} className="contents">
        <SheetHeader className="pb-4">
          <SheetTitle>{t('title')}</SheetTitle>
          <div className="flex items-center gap-2 pr-1">
            <Button
              hidden={!remoteMcpAction}
              onClick={form.handleSubmit(handleSubmit, onInvalid)}
              disabled={isPending}
              data-testid="remote-mcp-action-sheet-submit-button"
            >
              {t('button.save')}
            </Button>
            <IconButton
              variant="secondary"
              onClick={handleClose}
              disabled={isPending}
              data-testid="remote-mcp-action-sheet-close-button"
            >
              <X className="size-4" />
            </IconButton>
          </div>
        </SheetHeader>
        <div className="grid grid-cols-1 h-full overflow-hidden">
          <section
            ref={scrollContainerRef}
            className="space-y-7 relative px-1 h-full custom-scroll overflow-y-auto scroll-stable"
          >
            <div hidden={!remoteMcpAction} data-testid="remote-mcp-action-form-name-input">
              <FormField
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.name.label')}</FormLabel>
                    <FormDescription>{t('form.name.description')}</FormDescription>
                    <FormControl>
                      <Input autoFocus placeholder={t('form.name.placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <ConnectRemoteMcpForm
              connect={connect}
              remoteMcpAction={remoteMcpAction}
              withAccordion={!!remoteMcpAction}
              catalogEntryName={catalogEntryName}
            />
            <RemoteMcpActionTools
              form={form}
              isHidden={
                !remoteMcpAction ||
                (!!remoteMcpAction.oauth && remoteMcpAction.oauth.status !== 'authorized')
              }
              name="allowed_tools"
              recommendedSetup={recommendedSetupOf(remoteMcpAction)}
              thinkingDefaults={remoteMcpAction?.thinking_message_defaults}
            />
          </section>
        </div>
      </fieldset>
      <SheetConfirmationCloseDialog
        confirmationModalOpen={confirmationModalOpen}
        setConfirmationModalOpen={setConfirmationModalOpen}
        handleConfirmClose={handleConfirmClose}
      />
    </Form>
  );
};

export const RemoteMcpActionSheet = () => {
  const { isOpen, closeModal, params } = useModalStore();
  const { remoteMcpAction, catalogEntryName } = params || {};
  const closeRequestRef = useRef(closeModal);

  return (
    <Sheet
      open={isOpen(MODAL_NAMES.REMOTE_MCP_DIALOG)}
      onOpenChange={(open) => !open && closeRequestRef.current()}
    >
      <SheetContent
        id={MODAL_NAMES.REMOTE_MCP_DIALOG}
        aria-describedby={undefined}
        className="max-w-3xl w-full gap-0"
        tabIndex={undefined}
      >
        <RemoteMcpActionSheetContent
          remoteMcpAction={remoteMcpAction}
          catalogEntryName={catalogEntryName}
          closeModal={closeModal}
          closeRequestRef={closeRequestRef}
        />
      </SheetContent>
    </Sheet>
  );
};
